import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SettingsIcon,
  LogOutIcon,
  PanelLeftIcon,
  MenuIcon,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "@Store/authStore";
import { useProfileDetailsQuery } from "@/queries/useProfileDetailsQuery";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ProfileAvatar,
  normalizeAvatarUrl,
} from "@/polymet/components/profile-avatar";

// Admin header component with sidebar toggle and page title support

interface AdminHeaderProps {
  onToggleSidebar?: () => void;
  onMobileMenuToggle?: () => void;
  isSidebarCollapsed?: boolean;
  title?: string;
  description?: string;
}

// Route to page title mapping
const getPageTitle = (pathname: string): string => {
  // Handle dynamic routes with IDs
  if (pathname.startsWith("/customers/") && pathname !== "/customers") {
    return "Customer Details";
  }
  if (pathname.startsWith("/bookings/") && pathname !== "/bookings") {
    return "Booking Details";
  }
  if (pathname.startsWith("/drivers/edit/")) {
    return "Edit Driver";
  }
  if (
    pathname.startsWith("/drivers/") &&
    pathname !== "/drivers" &&
    pathname !== "/drivers/add"
  ) {
    return "Driver Details";
  }
  if (pathname.startsWith("/vehicles/edit/")) {
    return "Edit Vehicle";
  }
  if (
    pathname.startsWith("/vehicles/") &&
    pathname !== "/vehicles" &&
    pathname !== "/vehicles/add"
  ) {
    return "Vehicle Details";
  }

  // Static routes
  const routeTitles: Record<string, string> = {
    "/dashboard": "Dashboard",
    "/customers": "Customer Management",
    "/bookings": "Booking Management",
    "/drivers": "Driver Management",
    "/drivers/add": "Add Driver",
    "/vehicles": "Vehicle Management",
    "/vehicles/add": "Add Vehicle",
    "/pricing": "Pricing Setup",
    "/my-profile": "My Profile",
  };

  return routeTitles[pathname] || "";
};

// One-line subtitle under the header title. It replaced the per-page heading block, which repeated the page name
// a third time under the header and the sidebar (EA-375). Detail and form pages have none.
const routeDescriptions: Record<string, string> = {
  "/dashboard": "Welcome back! Here's what's happening with your fleet today.",
  "/bookings": "Manage and track all ride bookings",
  "/drivers": "View and manage all drivers, their status, assigned vehicles, and ride activity.",
  "/vehicles": "Manage fleet vehicles, track assignments, availability, and overall booking performance.",
  "/customers": "Monitor customer profiles, ride activity, and account status across the platform.",
  "/pricing": "Manage base fares and time-based pricing for vehicle categories.",
};

export function AdminHeader({
  onToggleSidebar,
  onMobileMenuToggle,
  title,
  description,
}: AdminHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const userData = useAuthStore((state) => state.userData);
  const removeUserAuthentication = useAuthStore(
    (state) => state.removeUserAuthentication
  );
  const {
    data: profile,
    isPending,
    isFetching,
    isFetchedAfterMount,
  } = useProfileDetailsQuery();
  // "/drivers/" renders the list too; without trimming it would read as "Driver Details" with no subtitle.
  const pathname = location.pathname.replace(/\/+$/, "") || "/";
  const pageTitle = title || getPageTitle(pathname);
  const pageDescription = description || routeDescriptions[pathname];
  const isProfileLoading = isPending || (isFetching && !isFetchedAfterMount);
  const avatarUrl =
    normalizeAvatarUrl(profile?.avatarUrl) ||
    normalizeAvatarUrl(userData?.imageUrl);

  const handleSignOut = () => {
    removeUserAuthentication();
    navigate("/login");
  };

  const userFirstName = profile?.firstName || userData?.firstName || "Admin";
  return (
    <header
      className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#E6E6E6] bg-white/80 px-4 shadow-sm backdrop-blur-md transition-[padding] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:h-20"
    >
      {/* Sidebar Toggle & Page Title */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onMobileMenuToggle}
          className="h-10 w-10 p-0 hover:bg-[#F5F5F5] transition-all lg:hidden"
        >
          <MenuIcon className="w-5 h-5 text-[#3A3A3A]" />
        </Button>

        {/* Desktop Sidebar Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleSidebar}
          className="h-10 w-10 p-0 hover:bg-[#F5F5F5] transition-all hidden lg:flex"
        >
          <PanelLeftIcon className="w-4 h-4 text-[#3A3A3A]" />
        </Button>
        {pageTitle && (
          <div className="flex min-w-0 flex-col">
            <h1 className="text-base lg:text-xl font-bold text-[#001D12] tracking-tight">
              {pageTitle}
            </h1>
            {pageDescription && (
              <p className="hidden truncate text-sm text-[#3A3A3A]/60 sm:block">
                {pageDescription}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Admin Actions */}
      <div className="flex items-center gap-4">
        {isProfileLoading ? (
          <div className="flex items-center gap-2 lg:gap-3 h-10 lg:h-12 px-2 lg:px-4 rounded-md bg-[#F5F5F5]">
            <Skeleton className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-[#001D12]/10" />
            <Skeleton className="hidden sm:block h-4 w-20 rounded-md bg-[#001D12]/10" />
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 lg:gap-3 h-10 lg:h-12 !border-none focus:outline-none px-2 lg:px-4 bg-[#F5F5F5] transition-all"
              >
                <ProfileAvatar
                  name={userFirstName}
                  src={avatarUrl}
                  className="w-8 h-8 lg:w-10 lg:h-10"
                  fallbackClassName="text-sm"
                />
                <span className="text-sm font-semibold text-[#001D12] hidden sm:inline">
                  {userFirstName}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-2">
              <DropdownMenuLabel className="text-base font-bold text-[#001D12]">
                Account
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#E6E6E6]" />
              <DropdownMenuItem
                className="py-3 px-3 cursor-pointer hover:bg-[#F5F5F5] rounded-lg transition-colors"
                onClick={() => navigate("/my-profile")}
              >
                <SettingsIcon className="w-4 h-4 mr-3 text-[#3A3A3A]" />
                <span className="font-medium">My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#E6E6E6]" />
              <DropdownMenuItem
                className="py-3 px-3 cursor-pointer hover:bg-red-50 rounded-lg transition-colors text-destructive"
                onClick={handleSignOut}
              >
                <LogOutIcon className="w-4 h-4 mr-3" />
                <span className="font-medium">Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
